import React from "react";
import {FC} from "react";
import DtoTable from "../../components/dto-table/dto-table";
import CertificateListModel from "../../models/dto/certificate-list-model";
import useSafeState from "../../utils/safe-state";
import {Breadcrumb, Button, Divider, Tooltip, Upload} from "antd";
import {Trans} from "@lingui/macro";
import {useAxios} from "../../utils/hooks";
import { AxiosResponse } from 'axios';
import { openNotification } from '../../utils/openNotification';
import {
    UploadOutlined,
    DownloadOutlined,
    EditTwoTone
} from "@ant-design/icons/lib";
import fileDownload from 'js-file-download';
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import Drawer from "../../components/drawer/drawer";
import CertificateEditDrawer from "../../forms/cargo-form/certificate-edit-drawer";

const CertificateScreen : FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [loading, setLoading] = useSafeState(false);
    const [trigger, setTrigger] = useSafeState(false);
    const [fileList, setFileList] = useSafeState<any[]>([]);
    const [queryModel, setQueryModel] = useSafeState<any>({});
    const [page, setPage] = useSafeState(1);
    const [pageSize, setPageSize] = useSafeState(5);

    const certificateEditDrawer = Drawer({
        title: <Trans>Edit Certificate</Trans>,
        children: <CertificateEditDrawer />,
        customClose: () => setTrigger(!trigger)
    });

    const downloadTemplate = () => {
        if (axiosInstance.current != null) {
            const request = {
                query: queryModel,
                start: (page - 1) * pageSize,
                length: pageSize
            };
            axiosInstance.current
                .post('/resource/stock/template', request, {
                    responseType: 'blob'
                })
                .then((response: AxiosResponse<any>) => {
                    fileDownload(response.data, 'stock_template.xlsx');
                })
                .catch((error: any) => {
                    console.error('Template download error:', error);
                    const message = (error && error.response && error.response.data && error.response.data.message)
                        ? error.response.data.message
                        : 'Failed to download template';
                    openNotification('error', message);
                });
        }
    };

    const uploadProps = {
        onRemove: (file: any) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file: any) => {
            const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            if (!isXlsx) {
                openNotification('customError', 'Only XLSX files are supported');
                return false;
            }

            if (axiosInstance.current != null) {
                setLoading(true);
                const formData = new FormData();
                formData.append('file', file);

                axiosInstance.current
                    .post('/resource/stock/upload-certificates', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    })
                    .then(() => {
                        openNotification('success', 'Certificates uploaded successfully');
                        setTrigger(!trigger);
                        setLoading(false);
                    })
                    .catch(function (error: any) {
                        const message = error && error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Upload failed';
                        openNotification('error', message);
                        setLoading(false);
                    });
            }

            return false;
        },
        fileList,
    };

    const action = (record: any) => (
        <Tooltip title={<Trans>Edit</Trans>}>
            <EditTwoTone
                twoToneColor={"#40a9ff"}
                className={"ActionButtons"}
                onClick={() => certificateEditDrawer.open("Edit", {
                    rollNumber: record.rollNumber,
                    manufacturerName: record.manufacturerName,
                    manufacturerId: record.manufacturerId,
                    certificateLink: record.certificateLink,
                    certificateQuality: record.certificateQuality,
                })}
            />
        </Tooltip>
    );

    const actions = {
        width: 60,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    return (<>
        <>
            <Breadcrumb style={{ marginBottom: 10 }}>
                <Breadcrumb.Item><Trans id={'Homepage'}/></Breadcrumb.Item>
                <Breadcrumb.Item><Trans id={'Certificate Upload'}></Trans></Breadcrumb.Item>
            </Breadcrumb>

            <DtoTable
                tableHeader={
                    <>
                        <Button onClick={downloadTemplate} style={{ float: 'left', marginTop: 3}}>
                            <ButtonWithIcon icon={<DownloadOutlined />} text={<Trans>Download Template</Trans>} />
                        </Button>
                        <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
                        <Upload {...uploadProps} accept=".xlsx" showUploadList={false} className="custom-upload" style={{ float: 'left'}}>
                            <Button loading={loading} style={{ float: 'left'}}>
                                <ButtonWithIcon icon={<UploadOutlined />} text={<Trans>Upload Certificates</Trans>} />
                            </Button>
                        </Upload>
                        <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
                    </>
                }
                model={CertificateListModel}
                action={actions}
                apiUrl="stock"
                trigger={trigger}
                allowExport={true}
                allowRefreshButton={true}
                pageSize={100}
                scroll={{x: 1500, y: "65vh"}}
                onQueryChange={(query) => setQueryModel(query)}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            />
            {certificateEditDrawer.component}
        </>
    </>);
}

export default CertificateScreen;
